/**
 * Places Phase 4 — ESPM utility-unit → canonical-base conversion (M4.8).
 *
 * ESPM meter units (my place_meter_types.allowed_units tokens) live in a
 * different token space than @releve/shared/units `convertCanonical` (the units
 * doctrine: `convertValue`/convert codes are disjoint from canonical names; the
 * BEDES layer bridges them — docs/UNITS.md). This is the ESPM-side shim: it maps
 * the ESPM unit tokens onto the canonical names, then delegates to
 * `convertCanonical` — reusing the same machinery `convertBedesValue` does,
 * including fuel energy-content (volume/mass → energy via a `ResourceType`).
 *
 * A utility reading's `quantity_internal` (the load-bearing value
 * get_place_utility_trend sums) is computed HERE, in the Hono layer, before the
 * write (SQL can't call convertCanonical). It THROWS on an unsupported/
 * unconvertible unit rather than returning null — the DB never stores a NULL
 * canonical value for a non-null quantity (the M4.4 CHECK).
 */
import { convertCanonical } from "../units/index.js";
/** Canonical BASE unit stored in place_utility_readings.quantity_internal, per domain. */
export const CANONICAL_BASE_UNIT = {
    energy: "KilowattHours", // kWh
    water: "CubicMeters", // m³
    waste: "Kilograms", // kg
};
/**
 * ESPM `allowed_units` token → `convertCanonical` canonical name.
 * (Tokens not listed here — e.g. cGal_UK, m3_per_day, MGal_per_day, mg_per_l —
 * are treatment-plant/flow/concentration units with no canonical volume/mass
 * equivalent; they throw as unsupported until a use case adds them.)
 */
export const ESPM_UNIT_TO_CANONICAL = {
    // energy
    kWh: "KilowattHours",
    MWh: "MegawattHours",
    kBtu: "KBtus",
    MBtu: "MBtus",
    GJ: "Gigajoules",
    therms: "Therms",
    ton_hours: "CHWTonHs",
    // gas/fuel volume (→ energy via ResourceType)
    cf: "CubicFeet",
    ccf: "CentaCubicFeet",
    kcf: "KiloCubicFeet",
    MCF: "MegaCubicFeet",
    // liquid volume
    Gallons_US: "UsGallons",
    Gallons_UK: "ImperialGallons",
    cGal_US: "HectoUsGallons",
    KGal_US: "KiloUsGallons",
    KGal_UK: "KiloImperialGallons",
    MGal_US: "MegaUsGallons",
    MGal_UK: "MegaImperialGallons",
    Liters: "Litres",
    m3: "CubicMeters",
    Kcm: "KiloCubicMeters",
    // waste volume (→ kg via waste_density_kg_per_yd3)
    CubicYards: "CubicYards",
    // mass (steam, coal, wood, waste)
    Lbs: "Pounds",
    KLbs: "Kilopounds",
    MLbs: "Megapounds",
    kg: "Kilograms",
    tons: "USTons",
    Tonnes: "Tonnes",
    // solid fuel volume
    Cords: "Cords",
};
/** meter_type.code → fuel ResourceType (for volume/mass → energy heating-value conversion). */
export const METER_TYPE_TO_RESOURCE = {
    natural_gas: "natural_gas",
    propane: "propane",
    fuel_oil_no1: "fuel_oil_1",
    fuel_oil_no2: "fuel_oil_2",
    fuel_oil_no4: "fuel_oil_4",
    fuel_oil_no5_6: "fuel_oil_6",
    diesel: "diesel",
    kerosene: "kerosene",
    district_steam: "steam",
    district_chilled_water_electric: "chilled_water",
    district_chilled_water_absorption_ng: "chilled_water",
    district_chilled_water_engine_ng: "chilled_water",
    district_chilled_water_other: "chilled_water",
    coal_anthracite: "coal_anthracite",
    coal_bituminous: "coal_bituminous",
    coke: "coke",
    wood: "wood",
};
export class UnsupportedUtilityUnitError extends Error {
    constructor(message) {
        super(message);
        this.name = "UnsupportedUtilityUnitError";
    }
}
/**
 * Convert a source utility quantity to its canonical base (kWh / m³ / kg).
 * Throws `UnsupportedUtilityUnitError` when the unit is unknown or can't be
 * converted (so the caller never persists a NULL quantity_internal).
 */
export function toCanonicalQuantity(quantity, sourceUnit, opts) {
    // Object.hasOwn, not a plain lookup: `constructor` / `__proto__` / `toString`
    // resolve to inherited members on an object literal, sail past an `undefined`
    // check, and blow up inside convertCanonical as a 500 instead of a clean 400.
    const source = Object.hasOwn(ESPM_UNIT_TO_CANONICAL, sourceUnit)
        ? ESPM_UNIT_TO_CANONICAL[sourceUnit]
        : undefined;
    if (source === undefined) {
        throw new UnsupportedUtilityUnitError(`Unknown utility unit '${sourceUnit}'`);
    }
    if (opts.domain === "waste") {
        // Waste weight units convert directly to kg.
        const asMass = convertCanonical(quantity, source, "Kilograms");
        if (asMass !== null)
            return round6(asMass);
        // A volume waste unit needs a density (kg per cubic yard).
        const asYd3 = convertCanonical(quantity, source, "CubicYards");
        if (asYd3 === null) {
            throw new UnsupportedUtilityUnitError(`Waste unit '${sourceUnit}' is neither a mass nor a supported volume`);
        }
        if (opts.wasteDensityKgPerYd3 == null || opts.wasteDensityKgPerYd3 <= 0) {
            throw new UnsupportedUtilityUnitError(`Volume waste unit '${sourceUnit}' requires waste_density_kg_per_yd3`);
        }
        return round6(asYd3 * opts.wasteDensityKgPerYd3);
    }
    const base = CANONICAL_BASE_UNIT[opts.domain];
    const resourceType = opts.domain === "energy" && opts.meterTypeCode
        ? (METER_TYPE_TO_RESOURCE[opts.meterTypeCode] ?? null)
        : null;
    const result = convertCanonical(quantity, source, base, resourceType);
    if (result === null) {
        throw new UnsupportedUtilityUnitError(`Cannot convert ${sourceUnit} → ${base}` +
            (resourceType ? ` (resource ${resourceType})` : "") +
            ` — unit/dimension mismatch`);
    }
    return round6(result);
}
/**
 * Units offered for a waste stream. Lives here, beside the conversion table, so
 * the account form and the readings grid can't drift from what's convertible.
 * The volume ones additionally require the account's waste_density_kg_per_yd3.
 */
export const WASTE_UNITS = [
    "tons", "Tonnes", "Lbs", "kg", "CubicYards", "m3", "Gallons_US", "Liters",
];
/** Waste units measured by volume — unusable without a density. */
export const VOLUME_WASTE_UNITS = ["CubicYards", "m3", "Gallons_US", "Liters"];
/** Lowercased ESPM token → canonical ESPM spelling, for case-insensitive resolution. */
const UNIT_BY_LOWER = Object.fromEntries(Object.keys(ESPM_UNIT_TO_CANONICAL).map((k) => [k.toLowerCase(), k]));
/**
 * Resolve a user-typed unit (e.g. from a CSV cell) to its canonical ESPM
 * spelling, case-insensitively — `kwh` → `kWh`. Returns null when unknown.
 * Importers should store the resolved spelling: `toCanonicalQuantity` looks up
 * an exact-case key, so an unresolved variant fails server-side.
 */
export function resolveUtilityUnit(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    if (Object.hasOwn(ESPM_UNIT_TO_CANONICAL, trimmed))
        return trimmed;
    // Object.hasOwn on the fallback too, for the same reason as the branch above:
    // a bare lookup resolves `constructor`/`__proto__` to inherited members, and
    // `?? null` doesn't catch a Function. The caller then treats that Function as
    // a resolved unit.
    const lower = trimmed.toLowerCase();
    return Object.hasOwn(UNIT_BY_LOWER, lower) ? UNIT_BY_LOWER[lower] : null;
}
/**
 * Why this unit can't be converted for this account, or null when it can.
 *
 * Deliberately implemented by *running* the real conversion, so a client-side
 * pre-flight check can never drift from what the server will actually accept.
 * A meter type's `allowed_units` is NOT a safe substitute: several seeded pairs
 * (coal in KLbs, propane in ccf, every unit of average_influent_flow) are
 * advertised as allowed yet have no conversion path, and volume waste units
 * additionally need the account's density.
 */
export function utilityUnitProblem(sourceUnit, opts) {
    try {
        toCanonicalQuantity(1, sourceUnit, opts);
        return null;
    }
    catch (e) {
        if (e instanceof UnsupportedUtilityUnitError)
            return e.message;
        throw e;
    }
}
/** Round to the NUMERIC(20,6) scale of quantity_internal so re-uploads are bit-identical. */
function round6(n) {
    return Math.round(n * 1e6) / 1e6;
}
//# sourceMappingURL=utilityUnits.js.map